import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { FashSnapProductData } from '../../types';
import { environmentConfig } from '../../config/environment';
import { BodyTypeSelector, BodyTypeInfo } from '../../components/BodyTypeSelector/BodyTypeSelector';
import { AvatarPreview } from '../../components/AvatarPreview/AvatarPreview';
import { AvatarRevealLoader } from '../../components/Loader/AvatarRevealLoader';
import { WaitingOrchestrator } from '../../components/WaitingScreen';
import { PhotoUpload } from '../../components/shared/PhotoUpload';
import { UserStatsStep } from '../../components/shared/UserStatsStep';
import { ErrorStep } from '../../components/shared/ErrorStep';
import { FashSnapResult } from '../../components/shared/FashSnapResult';
import { indexedDBService, StoredUser } from '../../services/IndexedDBService';
import './FashSnapPage.css';

/**
 * FashSnapPage - Virtual Try-On with Avatar
 * 
 * Flow for NEW users: upload-photos → user-stats → body-type → generating-avatar → avatar-preview → generating → results
 * Flow for RETURNING users: Check IndexedDB → generating → results (using saved avatar)
 */

type SnapStep =
    | 'loading'
    | 'upload-photos'
    | 'user-stats'
    | 'body-type'
    | 'generating-avatar'
    | 'avatar-preview'
    | 'generating'
    | 'results'
    | 'error';

const ACCESSORY_CATEGORY_TOKENS = ['accessory', 'aksesuar', 'watch', 'saat'];
const ACCESSORY_RESULT_LABELS = ['Takarken', 'Yakın Çekim', 'Tam Stil', 'Sosyal Ortam'];

const isAccessoryCategory = (category?: string | null) => {
    if (!category) return false;
    const normalized = category.toLocaleLowerCase('tr-TR');
    return ACCESSORY_CATEGORY_TOKENS.some(token => normalized.includes(token));
};

const normalizeSnapStyle = (style?: string | null) => {
    const normalized = style?.toLocaleLowerCase('tr-TR');
    if (normalized === 'comfort' || normalized === 'business' || normalized === 'daily') {
        return normalized;
    }
    return 'daily';
};

const FashSnapPage: React.FC = () => {
    const navigate = useNavigate();
    const { state, dispatch } = useAppContext();
    const productData: FashSnapProductData | null = React.useMemo(() => {
        return state.fashSnapProduct || (state.demoMode ? { productId: 'DEMO-SNAP-001', category: 'clothing', style: 'casual' } : null);
    }, [state.fashSnapProduct, state.demoMode]);
    const productCategory = productData?.category || productData?.productMeta?.category;
    const isAccessoryMode = isAccessoryCategory(productCategory);
    const snapStyle = normalizeSnapStyle(productData?.style || productData?.productMeta?.style);
    
    // Helper Maps for Demo Mode
    const genderMap: Record<string, 'male'|'female'> = React.useMemo(() => ({
        melis: 'female',
        can: 'male',
        emre: 'male'
    }), []);
    const bodyTypeMap: Record<string, string> = React.useMemo(() => ({
        melis: 'pear',
        can: 'trapezoid',
        emre: 'rectangle'
    }), []);
    const statsMap = React.useMemo(() => ({
        melis: { age: 30, height: 165, weight: 58 },
        can: { age: 28, height: 180, weight: 76 },
        emre: { age: 33, height: 185, weight: 82 }
    }), []);

    const isDemoActive = !!state.demoMode;
    const demoPersonKey = state.demoPersonId || 'melis';

    // Step State
    const [step, setStep] = useState<SnapStep>(isDemoActive ? 'upload-photos' : 'loading');
    const [isCheckingUser, setIsCheckingUser] = useState(!isDemoActive);
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // User Data State
    const [storedUser, setStoredUser] = useState<StoredUser | null>(null);
    const [gender, setGender] = useState<'female' | 'male' | null>(
        isDemoActive ? (genderMap[demoPersonKey] || 'female') : null
    );
    const [selectedBodyType, setSelectedBodyType] = useState<string | null>(
        isDemoActive ? (bodyTypeMap[demoPersonKey] || 'hourglass') : null
    );
    const [selectedBodyTypeInfo, setSelectedBodyTypeInfo] = useState<BodyTypeInfo | null>(
        isDemoActive ? {
            id: bodyTypeMap[demoPersonKey] || 'hourglass',
            title: 'Otomatik Tespit',
            description: 'Analiz ile belirlenen vücut ölçüleri',
            imagePath: ''
        } : null
    );
    const [userStats, setUserStats] = useState<{ age: number; height: number; weight: number } | null>(
        isDemoActive ? (statsMap[demoPersonKey as keyof typeof statsMap] || { age: 25, height: 170, weight: 65 }) : null
    );
    const [selfieImage, setSelfieImage] = useState<string | null>(
        isDemoActive ? `/assets/showcase/selfie_${demoPersonKey}.jpg` : null
    );
    const [bodyImage, setBodyImage] = useState<string | null>(null);
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

    // Result State
    const [generatedImages, setGeneratedImages] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    
    // Waiting Screen State
    const isResultReadyRef = useRef<boolean>(false);
    const currentProductSkuRef = useRef<string | null>(null);

    // API URL helper
    const getApiUrl = useCallback(() => {
        let apiUrl = environmentConfig.api.combinationsApi || 'https://fashion-connect-production.up.railway.app';
        if (apiUrl.endsWith('/api/v1')) {
            apiUrl = apiUrl.replace('/api/v1', '');
        }
        return apiUrl;
    }, []);

    const useLegacyAiEndpoints =
        process.env.REACT_APP_USE_LEGACY_AI_ENDPOINTS === 'true' ||
        (typeof window !== 'undefined' && window.sessionStorage.getItem('FC_USE_LEGACY_AI_ENDPOINTS') === 'true');

    // Generate Grid Images
    const generateGridImages = useCallback(async (
        avatar: string,
        explicitUserId?: string,
        profile?: { gender?: 'female' | 'male' | null; bodyType?: string | null }
    ) => {
        if (!productData) {
            setError('Ürün bulunamadı');
            setStep('error');
            return;
        }

        isResultReadyRef.current = false;
        currentProductSkuRef.current = productData.productId;
        setStep('generating');
        setError(null);
        window.parent.postMessage({ type: 'GENERATION_STARTED' }, '*');

        if (state.demoMode) {
            console.log('🎬 [FashSnap] Running in demo mode, returning pre-cached grid images');
            await new Promise(resolve => setTimeout(resolve, 3000));
            const personKey = state.demoPersonId || 'melis';
            const cacheUrls = [
                `/assets/showcase/snap_${personKey}_1.jpg`,
                `/assets/showcase/snap_${personKey}_2.jpg`,
                `/assets/showcase/snap_${personKey}_3.jpg`,
                `/assets/showcase/snap_${personKey}_4.jpg`
            ];
            setGeneratedImages(cacheUrls);
            isResultReadyRef.current = true;
            window.parent.postMessage({ type: 'GENERATION_COMPLETED' }, '*');
            return;
        }

        try {
            console.log('🎬 [FashSnap] Generating grid images...');
            const response = await fetch(`${getApiUrl()}${useLegacyAiEndpoints ? '/api/v1/fashsnap/generate-grid' : '/api/v1/media/image/jobs'}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: productData.productId,
                    userId: explicitUserId || storedUser?.userId,
                    selfieUrl: selfieImage || avatar,
                    avatarUrl: avatar,
                    category: productCategory,
                    style: snapStyle,
                    mode: isAccessoryMode ? 'accessory' : 'try-on',
                    gender: profile?.gender || gender,
                    bodyType: profile?.bodyType || selectedBodyType,
                }),
            });

            const data = await response.json();
            if (!response.ok || (!data.success && data.status !== 'completed')) {
                throw new Error(data.error || 'Görsel üretimi başarısız');
            }

            if (data.images?.length >= 4) {
                setGeneratedImages(data.images);
                isResultReadyRef.current = true;
                window.parent.postMessage({ type: 'GENERATION_COMPLETED' }, '*');
            } else {
                throw new Error('Yeterli görsel üretilemedi');
            }
        } catch (err: any) {
            console.error('❌ [FashSnap] Grid generation error:', err);
            setError(err.message || 'Görsel üretilirken hata oluştu');
            setStep('error');
            window.parent.postMessage({ type: 'GENERATION_FAILED', payload: { error: err } }, '*');
        }
    }, [selfieImage, storedUser, productData, productCategory, snapStyle, isAccessoryMode, gender, selectedBodyType, getApiUrl, useLegacyAiEndpoints, state.demoMode, state.demoPersonId]);

    // Generate Avatar
    const generateAvatar = useCallback(async (
        selfie: string,
        overrides?: {
            gender?: 'female' | 'male';
            bodyType?: string;
            bodyTypeInfo?: BodyTypeInfo;
            stats?: { age: number; height: number; weight: number };
        }
    ) => {
        const activeGender = overrides?.gender || gender;
        const activeBodyType = overrides?.bodyType || selectedBodyType;
        const activeBodyTypeInfo = overrides?.bodyTypeInfo || selectedBodyTypeInfo;
        const activeStats = overrides?.stats || userStats;

        if (!activeGender || !activeBodyType || !activeBodyTypeInfo) {
            setError('Gerekli veriler eksik');
            setStep('error');
            return;
        }

        setStep('generating-avatar');
        setError(null);

        if (state.demoMode) {
            console.log('🎨 [FashSnap] Running in demo mode, returning pre-cached avatar');
            await new Promise(resolve => setTimeout(resolve, 2000));
            const personKey = state.demoPersonId || 'melis';
            const cacheUrl = `/assets/showcase/avatar_${personKey}.jpg`;
            setAvatarUrl(cacheUrl);
            setStep('avatar-preview');
            return;
        }

        try {
            console.log('🎨 [FashSnap] Generating avatar...');
            if (!state.brandId) {
                throw new Error('Brand ID eksik. SDK init kontrol edilmeli.');
            }

            const createPath = useLegacyAiEndpoints ? '/api/v1/fash-motion/generate-avatar' : '/api/v1/media/image/avatar/jobs';
            const response = await fetch(`${getApiUrl()}${createPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    selfie: selfie,
                    gender: activeGender,
                    bodyType: activeBodyType,
                    age: activeStats?.age,
                    height: activeStats?.height,
                    weight: activeStats?.weight,
                    brandId: state.brandId,
                    userId: storedUser?.userId,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Avatar oluşturulamadı');
            }

            let avatar = data.avatarUrl;
            if (data.jobId && !avatar) {
                const statusPath = useLegacyAiEndpoints
                    ? `/api/v1/fash-motion/generate-avatar-status/${data.jobId}`
                    : `/api/v1/media/image/avatar/jobs/${data.jobId}`;

                for (let attempt = 0; attempt < 100; attempt += 1) {
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    const statusResponse = await fetch(`${getApiUrl()}${statusPath}`);
                    const statusData = await statusResponse.json();

                    if (!statusResponse.ok) {
                        throw new Error(statusData.error || 'Avatar durumu alınamadı');
                    }
                    if (statusData.status === 'completed' && statusData.avatarUrl) {
                        avatar = statusData.avatarUrl;
                        break;
                    }
                    if (statusData.status === 'failed') {
                        throw new Error(statusData.error || 'Avatar oluşturulamadı');
                    }
                }
            }

            if (!avatar) {
                throw new Error('Avatar oluşturma zaman aşımına uğradı');
            }

            console.log('✅ [FashSnap] Avatar generated:', avatar);
            setAvatarUrl(avatar);
            setStep('avatar-preview');
        } catch (err: any) {
            console.error('❌ [FashSnap] Avatar generation error:', err);
            setError(err.message || 'Avatar oluşturulurken hata oluştu');
            setStep('error');
        }
    }, [gender, selectedBodyType, selectedBodyTypeInfo, getApiUrl, userStats, state.brandId, storedUser, useLegacyAiEndpoints, state.demoMode, state.demoPersonId]);

    // ============================================
    // INITIALIZATION: Check for existing user
    // ============================================
    // Sync demo person if changed
    useEffect(() => {
        if (state.demoMode) {
            const personKey = state.demoPersonId || 'melis';
            console.log(`👤 [FashSnap] Demo person selected: ${personKey}`);
            const genderMap: Record<string, 'male'|'female'> = {
                melis: 'female',
                can: 'male',
                emre: 'male'
            };
            const bodyTypeMap: Record<string, string> = {
                melis: 'pear',
                can: 'trapezoid',
                emre: 'rectangle'
            };
            const statsMap = {
                melis: { age: 30, height: 165, weight: 58 },
                can: { age: 28, height: 180, weight: 76 },
                emre: { age: 33, height: 185, weight: 82 }
            };

            setSelfieImage(`/assets/showcase/selfie_${personKey}.jpg`);
            setGender(genderMap[personKey] || 'female');
            setSelectedBodyType(bodyTypeMap[personKey] || 'hourglass');
            setUserStats(statsMap[personKey as keyof typeof statsMap] || { age: 25, height: 170, weight: 65 });
            setStep('upload-photos');
            setIsCheckingUser(false);
        }
    }, [state.demoMode, state.demoPersonId]);

    useEffect(() => {
        const checkExistingUser = async () => {
            if (state.demoMode) {
                console.log('🔍 [FashSnap] Running in demo mode, bypassing IndexedDB check');
                setStep('upload-photos');
                setIsCheckingUser(false);
                return;
            }
            try {
                console.log('🔍 [FashSnap] Checking for existing user...');
                const user = await indexedDBService.getUser();

                if (user?.userId && user?.avatarUrl) {
                    console.log('✅ [FashSnap] Found existing user:', user.userId);
                    setStoredUser(user);
                    setAvatarUrl(user.avatarUrl);
                    setGender(user.gender || null);
                    setSelectedBodyType(user.bodyType || null);

                    // Skip to result generation
                    setStep('generating');
                    generateGridImages(user.avatarUrl, user.userId, {
                        gender: user.gender,
                        bodyType: user.bodyType,
                    });
                } else {
                    console.log('📝 [FashSnap] No existing user, starting fresh');
                    setStep('upload-photos');
                }
            } catch (error) {
                console.error('❌ [FashSnap] Error checking user:', error);
                setStep('upload-photos');
            } finally {
                setIsCheckingUser(false);
            }
        };

        if (productData) {
            checkExistingUser();
        }
        // This init must not re-run when wizard form state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productData?.productId]);

    // ============================================
    // HANDLERS
    // ============================================

    // Handle back navigation
    const handleBack = () => {
        switch (step) {
            case 'upload-photos':
                navigate(-1);
                break;
            case 'user-stats':
                setStep('upload-photos');
                break;
            case 'body-type':
                setStep('user-stats');
                break;
            case 'avatar-preview':
                setStep('upload-photos');
                break;
            case 'results':
                setStep('upload-photos');
                break;
            case 'error':
                setStep('upload-photos');
                break;
            default:
                break;
        }
    };

    // Step 1: Photo Upload Complete
    const handlePhotoUploadComplete = useCallback((data: {
        selfieBase64: string;
        bodyBase64: string | null;
        hasBodyPhoto: boolean;
    }) => {
        console.log('📸 [FashSnap] Photo upload complete. Has Body:', data.hasBodyPhoto);
        setSelfieImage(data.selfieBase64);
        setBodyImage(data.bodyBase64);
        setStep('user-stats');
    }, []);

    // Step 2: User Stats Complete — detect gender from selfie then proceed
    const handleUserStatsComplete = useCallback(async (data: {
        age: number;
        height: number;
        weight: number;
    }) => {
        console.log('📊 [FashSnap] Stats complete:', data);
        setUserStats(data);
        setIsAnalyzing(true);

        if (!selfieImage) {
            console.error('Selfie image missing in stats complete');
            setStep('error');
            setIsAnalyzing(false);
            return;
        }

        if (state.demoMode) {
            console.log('📊 [FashSnap] Demo mode detected, bypassing validation API calls.');
            setIsAnalyzing(false);
            const activeGender = gender || 'female';
            const activeBodyType = selectedBodyType || 'hourglass';
            const activeBodyTypeInfo: BodyTypeInfo = selectedBodyTypeInfo || {
                id: activeBodyType,
                title: 'Otomatik Tespit',
                description: 'Analiz ile belirlenen vücut ölçüleri',
                imagePath: ''
            };
            generateAvatar(selfieImage, {
                gender: activeGender,
                bodyType: activeBodyType,
                bodyTypeInfo: activeBodyTypeInfo,
                stats: data,
            });
            return;
        }

        try {
            let detectedGender: 'female' | 'male' = 'female';

            if (bodyImage) {
                console.log('🧠 [FashSnap] Validating selfie and body photo...');
                const validatePath = useLegacyAiEndpoints ? '/api/v1/fash-play/validate-fast-mode' : '/api/v1/media/image/validate-fast-mode';
                const response = await fetch(`${getApiUrl()}${validatePath}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        selfieImage,
                        fullBodyImage: bodyImage,
                        metadata: data,
                    })
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Analiz başarısız');
                if (!result.isSelfieValid) throw new Error(result.selfieError || 'Selfie geçersiz');
                if (!result.isBodyValid) throw new Error(result.bodyError || 'Boy fotoğrafı geçersiz');

                detectedGender = result.detectedGender === 'male' ? 'male' : 'female';
                setGender(detectedGender);

                if (result.detectedRegions) {
                    const autoBodyTypeId = `${detectedGender}-${result.detectedRegions.upper}-${result.detectedRegions.waist}-${result.detectedRegions.lower}`;
                    const autoBodyTypeInfo: BodyTypeInfo = {
                        id: autoBodyTypeId,
                        title: 'Otomatik Tespit',
                        description: 'Analiz ile belirlenen vücut ölçüleri',
                        imagePath: ''
                    };
                    setSelectedBodyType(autoBodyTypeId);
                    setSelectedBodyTypeInfo(autoBodyTypeInfo);
                    setIsAnalyzing(false);
                    generateAvatar(selfieImage, {
                        gender: detectedGender,
                        bodyType: autoBodyTypeId,
                        bodyTypeInfo: autoBodyTypeInfo,
                        stats: data,
                    });
                    return;
                }
            } else {
                console.log('👤 [FashSnap] Detecting gender from selfie...');
                const validatePath = useLegacyAiEndpoints ? '/api/v1/fash-play/validate-selfie' : '/api/v1/media/image/validate-selfie';
                const response = await fetch(`${getApiUrl()}${validatePath}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ selfieImage })
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Cinsiyet analizi başarısız');
                if (!result.valid) throw new Error(result.error || 'Selfie geçersiz');

                detectedGender = result.detectedGender === 'male' ? 'male' : 'female';
            }

            console.log('✅ [FashSnap] Gender detected:', detectedGender);
            setGender(detectedGender);
            setIsAnalyzing(false);
            setStep('body-type');
        } catch (err: any) {
            console.error('❌ [FashSnap] Gender detection error:', err);
            setError(err.message || 'Cinsiyet analizi sırasında hata oluştu');
            setIsAnalyzing(false);
            setStep('error');
        }
    }, [selfieImage, bodyImage, gender, selectedBodyType, selectedBodyTypeInfo, getApiUrl, useLegacyAiEndpoints, generateAvatar, state.demoMode]);

    // Step 3: Body Type Selection
    const handleBodyTypeSelect = useCallback((bodyTypeId: string, bodyTypeInfo: BodyTypeInfo) => {
        console.log('🧍 [FashSnap] Body type selected:', bodyTypeId);
        setSelectedBodyType(bodyTypeId);
        setSelectedBodyTypeInfo(bodyTypeInfo);
    }, []);

    const handleBodyTypeContinue = useCallback(() => {
        if (selectedBodyType && selfieImage && gender) {
            generateAvatar(selfieImage);
        }
    }, [selectedBodyType, selfieImage, gender, generateAvatar]);


    // Avatar Approval - Create user and save to IndexedDB
    const handleAvatarApprove = useCallback(async () => {
        if (!avatarUrl || !gender || !selectedBodyType) return;

        if (state.demoMode) {
            console.log('✅ [FashSnap] Avatar approved in demo mode, bypassing backend creation.');
            generateGridImages(avatarUrl, 'demo-user');
            return;
        }

        try {
            console.log('✅ [FashSnap] Avatar approved, creating user...');

            // Create user in backend
            const response = await fetch(`${getApiUrl()}/api/v1/avatar-users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gender,
                    body_type: selectedBodyType,
                    avatar_url: avatarUrl,
                    front_image_url: selfieImage,
                    created_product_id: productData?.productId,
                    age: userStats?.age,
                    height: userStats?.height,
                    weight: userStats?.weight
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Kullanıcı oluşturulamadı');
            }

            const userId = data.user.id;
            console.log('✅ [FashSnap] User created:', userId);

            // Save to IndexedDB
            await indexedDBService.setUser({
                userId,
                avatarUrl,
                gender,
                bodyType: selectedBodyType,
                timestamp: Date.now(),
                age: userStats?.age,
                height: userStats?.height,
                weight: userStats?.weight
            });

            console.log('✅ [FashSnap] User saved to IndexedDB');

            // Proceed to grid generation
            generateGridImages(avatarUrl, userId);

        } catch (err: any) {
            console.error('❌ [FashSnap] User creation error:', err);
            generateGridImages(avatarUrl); // On error, proceed without ID (no caching)
        }
    }, [avatarUrl, gender, selectedBodyType, selfieImage, productData, getApiUrl, userStats, generateGridImages, state.demoMode]);

    const handleAvatarRetry = useCallback(() => {
        setAvatarUrl(null);
        setStep('upload-photos');
    }, []);

    // Check if result is ready for WaitingOrchestrator
    const checkResultReady = useCallback(async (): Promise<boolean> => {
        return isResultReadyRef.current;
    }, []);

    // Handle WaitingOrchestrator completion
    const handleWaitingComplete = useCallback(() => {
        if (generatedImages.length > 0) {
            setStep('results');
        }
    }, [generatedImages]);

    // Generate Grid Images

    // Reset user (for testing / new avatar)
    const handleResetUser = async () => {
        await indexedDBService.clearUser();
        setStoredUser(null);
        setAvatarUrl(null);
        setGender(null);
        setSelectedBodyType(null);
        setSelectedBodyTypeInfo(null);
        setSelfieImage(null);
        setBodyImage(null);
        setGeneratedImages([]);
        setStep('upload-photos');
    };

    // ============================================
    // RENDER
    // ============================================

    const renderContent = () => {
        // Loading state while checking for existing user
        if (isCheckingUser || step === 'loading') {
            return (
                <div className="fashsnap-content">
                    <div className="fashsnap-loading">
                        <div className="fashsnap-loading__spinner" />
                        <p>Yükleniyor...</p>
                    </div>
                </div>
            );
        }

        switch (step) {
            case 'upload-photos':
                return (
                    <div className="fashsnap-content">
                        <PhotoUpload
                            onComplete={handlePhotoUploadComplete}
                            showBodyUpload={true}
                            title="Fotoğraflarınızı Yükleyin"
                            subtitle="Avatar oluşturmak için selfie zorunlu; boy fotoğrafını ekleyebilir veya geçebilirsiniz."
                            demoMode={false}
                            initialSelfie={selfieImage}
                            onSelectDemoPerson={(personId) => {
                                dispatch({
                                    type: 'HANDLE_MESSAGE',
                                    payload: {
                                        type: 'DEMO_SELECT_PERSON',
                                        payload: { personId }
                                    }
                                });
                            }}
                        />
                    </div>
                );

            case 'user-stats':
                return (
                    <div className="fashsnap-content">
                        <UserStatsStep
                            onComplete={handleUserStatsComplete}
                            initialData={userStats || undefined}
                            isLoading={isAnalyzing}
                        />
                    </div>
                );

            case 'body-type':
                return (
                    <>
                        <div className="fashsnap-content">
                            {gender && (
                                <BodyTypeSelector
                                    gender={gender}
                                    onSelect={handleBodyTypeSelect}
                                    selectedBodyType={selectedBodyType || undefined}
                                    apiBaseUrl={getApiUrl()}
                                />
                            )}
                        </div>
                        <div className="fashsnap-bottom-actions">
                            <button
                                className="fashsnap-cta-primary"
                                onClick={handleBodyTypeContinue}
                                disabled={!selectedBodyType}
                            >
                                Devam Et
                            </button>
                        </div>
                    </>
                );

            case 'generating-avatar':
                return (
                    <AvatarRevealLoader
                        selfieUrl={selfieImage || undefined}
                        gender={gender || undefined}
                    />
                );

            case 'avatar-preview':
                return (
                    <div className="fashsnap-content">
                        {avatarUrl && gender && selectedBodyType && selectedBodyTypeInfo && selfieImage && (
                            <AvatarPreview
                                avatarData={{
                                    avatarUrl: avatarUrl,
                                    selfieUrl: selfieImage,
                                    gender: gender,
                                    bodyType: selectedBodyType,
                                    bodyTypeTitle: selectedBodyTypeInfo.title,
                                }}
                                onApprove={handleAvatarApprove}
                                onReject={handleAvatarRetry}
                            />
                        )}
                    </div>
                );

            case 'generating':
                return state.brandId ? (
                    <WaitingOrchestrator
                        brandId={state.brandId}
                        waitingType="fashsnap"
                        productSku={currentProductSkuRef.current || productData?.productId}
                        userId={storedUser?.userId}
                        onComplete={handleWaitingComplete}
                        onResultReady={() => console.log('🎉 [FashSnap] Result ready!')}
                        checkResultReady={checkResultReady}
                    />
                ) : (
                    <AvatarRevealLoader
                        selfieUrl={selfieImage || avatarUrl || undefined}
                        gender={gender || undefined}
                    />
                );

            case 'results':
                return (
                    <FashSnapResult
                        generatedImages={generatedImages}
                        onResetUser={handleResetUser}
                        labels={isAccessoryMode ? ACCESSORY_RESULT_LABELS : undefined}
                    />
                );

            case 'error':
                return (
                    <ErrorStep
                        error={error}
                        onRetry={() => setStep('upload-photos')}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div className="fashsnap-container">
            {/* Header */}
            {step !== 'results' && (
                <header className="fashsnap-header">
                    <button
                        className="fashsnap-header__back"
                        onClick={handleBack}
                        disabled={step === 'generating' || step === 'generating-avatar' || step === 'upload-photos' || isCheckingUser}
                    >
                        ←
                    </button>
                    <div className="fashsnap-header__title-group">
                        <h1 className="fashsnap-header__title">FashSnap</h1>
                        <p className="fashsnap-header__subtitle">
                            {isAccessoryMode ? 'Saat ve Aksesuar Deneme' : 'Sanal Kıyafet Deneme'}
                        </p>
                    </div>
                </header>
            )}

            {/* Main Content */}
            {renderContent()}
        </div>
    );
};

export default FashSnapPage;
